import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-kit";
import { Loader } from "@/components/Loader";
import { Clock, Activity, Timer, Trophy, Gauge, TrendingUp } from "lucide-react";

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics").then((r) => setData(r.data)).catch(() => setData({})); }, []);
  if (!data) return <Loader />;

  const stats = [
    { icon: Timer, label: "Avg response time", value: `${data.avg_response_sec}s`, color: "#F97316" },
    { icon: Activity, label: "Total emergencies", value: data.total_emergencies, color: "#7c3aed" },
    { icon: Clock, label: "Average ETA", value: `${data.avg_eta_min} min`, color: "#0891b2" },
    { icon: Gauge, label: "Community readiness", value: `${data.community_readiness}%`, color: "#16A34A" },
  ];

  return (
    <div className="ll-fade-up" data-testid="analytics-screen">
      <PageHeader title="Analytics" subtitle="Guardian AI performance across your community." />
      <div className="px-5">
        <span className="inline-block text-[11px] font-bold uppercase tracking-wider bg-[#0E7C66]/10 text-[#0E7C66] px-3 py-1 rounded-full mb-4">Live data</span>
        <div className="grid grid-cols-2 gap-3">
          {stats.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${s.color}1a` }}><s.icon className="w-5 h-5" style={{ color: s.color }} /></div>
              <p className="text-3xl font-black font-head">{s.value}</p>
              <p className="text-xs text-[#111827]/50 font-semibold mt-0.5">{s.label}</p>
            </motion.div>
          ))}
        </div>

        <Card className="mt-4">
          <h3 className="text-lg font-bold font-head flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-[#F97316]" /> Most active responders</h3>
          <div className="space-y-3">
            {(data.most_active_responders || []).map((r, i) => (
              <div key={r.name} className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? "bg-[#F97316] text-white" : "bg-[#C4B5FD]/30"}`}>{i + 1}</div>
                <span className="flex-1 font-semibold">{r.name}</span>
                <div className="flex items-center gap-2 flex-1">
                  <div className="h-2 rounded-full bg-[#F97316]" style={{ width: `${Math.min(100, r.responses * 18)}%` }} />
                  <span className="text-sm text-[#111827]/50 font-semibold">{r.responses}</span>
                </div>
              </div>
            ))}
            {(!data.most_active_responders || data.most_active_responders.length === 0) && <p className="text-[#111827]/50 text-sm">No responder data yet.</p>}
          </div>
        </Card>

        <Card className="mt-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-[#16A34A]" />
            <div><p className="font-bold">{data.resolved_count} emergencies resolved</p><p className="text-sm text-[#111827]/50">Across all recorded events.</p></div>
          </div>
        </Card>
      </div>
    </div>
  );
}
